import { DropdownMenuContent, DropdownMenuItem } from '@components/ui/dropdown-menu';
import { SimpleTooltipV2 } from '@components/ui/tooltip';
import {
  resolveActionLabel,
  resolveCategoryLabel,
  transformActionForHumanCall,
} from '@utils/caseActions';
import React from 'react';

import type { CategorizedActionsResult } from '@/types/actions';

interface CaseActionsDropdownItemsProps {
  categorizedActions: CategorizedActionsResult;
  caseState?: string;
  onActionSelect: (action: string) => void;
}

/** Renders the menu items for case actions without a wrapping DropdownMenuContent. */
export const CaseActionsDropdownItems: React.FC<CaseActionsDropdownItemsProps> = ({
  categorizedActions,
  caseState,
  onActionSelect,
}) => {
  return (
    <>
      {categorizedActions.map((actionCategory, index) => {
        const showSeparator = index > 0;

        return (
          <React.Fragment key={actionCategory.category}>
            {/* Separator between sections */}
            {showSeparator && <div className="my-1 h-px bg-border" />}

            {/* Category header - only show if categoryLabel is not empty */}
            {actionCategory.categoryLabel && (
              <DropdownMenuItem className="font-medium cursor-default" disabled>
                {resolveCategoryLabel(actionCategory.categoryLabel, caseState)}
              </DropdownMenuItem>
            )}

            {/* Action items */}
            {actionCategory.actions.map(action => {
              const label = resolveActionLabel(action, caseState);
              // Disabled items (e.g. Start Human Audit while the summary is
              // regenerating) render blurred + non-clickable, with a tooltip
              // explaining why. We deliberately avoid DropdownMenuItem's `disabled`
              // (it sets pointer-events:none, which would suppress the tooltip).
              if (action.disabled) {
                return (
                  <SimpleTooltipV2
                    key={action.value}
                    content={action.disabledTooltip ?? ''}
                    side="left"
                  >
                    <div
                      role="menuitem"
                      aria-disabled="true"
                      onClick={e => e.stopPropagation()}
                      className="flex cursor-not-allowed select-none items-center rounded-sm px-2 py-1.5 pl-2 text-sm text-nowrap opacity-50 blur-[0.6px]"
                    >
                      {label}
                    </div>
                  </SimpleTooltipV2>
                );
              }
              return (
                <DropdownMenuItem
                  key={action.value}
                  onClick={e => {
                    e.stopPropagation();
                    onActionSelect(transformActionForHumanCall(action.value, caseState));
                  }}
                  className="cursor-pointer pl-2 text-nowrap"
                >
                  {label}
                </DropdownMenuItem>
              );
            })}
          </React.Fragment>
        );
      })}
    </>
  );
};

interface CaseActionsDropdownProps extends CaseActionsDropdownItemsProps {
  onClick?: (e: React.MouseEvent) => void;
}

export const CaseActionsDropdown: React.FC<CaseActionsDropdownProps> = ({
  categorizedActions,
  caseState,
  onActionSelect,
  onClick,
}) => {
  return (
    <DropdownMenuContent align="end" className="min-w-56" onClick={onClick}>
      <CaseActionsDropdownItems
        categorizedActions={categorizedActions}
        caseState={caseState}
        onActionSelect={onActionSelect}
      />
    </DropdownMenuContent>
  );
};
